import { Link } from "react-router-dom";
import type { DeskGlance } from "@/shared/api/desk";

function formatOutstanding(
  outstanding: string | undefined,
  currencyCode: string,
): string | null {
  if (outstanding == null) return null;
  const n = Number(outstanding);
  if (!Number.isFinite(n) || n <= 0) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currencyCode || "PHP",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currencyCode} ${outstanding}`;
  }
}

type Chip = {
  key: string;
  to: string;
  label: string;
  value: string;
};

/** Collapsed owner glance — queue counts deep-link — plan/44 */
export function OwnerGlanceBand(props: {
  companySlug: string;
  currencyCode: string;
  glance: DeskGlance | null;
  loading?: boolean;
}) {
  const { companySlug, currencyCode, glance, loading } = props;
  if (loading && !glance) {
    return (
      <p className="mt-6 text-sm text-[var(--color-muted)]">Loading glance…</p>
    );
  }
  if (!glance) return null;

  const chips: Chip[] = [];
  if (glance.toCollect) {
    const money = formatOutstanding(glance.toCollect.outstanding, currencyCode);
    chips.push({
      key: "toCollect",
      to: `/c/${companySlug}/sales/to-collect`,
      label: "To collect",
      value:
        money != null
          ? `${glance.toCollect.count} · ${money}`
          : String(glance.toCollect.count),
    });
  }
  if (glance.toDeliver) {
    chips.push({
      key: "toDeliver",
      to: `/c/${companySlug}/sales/to-deliver`,
      label: "To deliver",
      value: String(glance.toDeliver.count),
    });
  }
  if (glance.toPay) {
    const money = formatOutstanding(glance.toPay.outstanding, currencyCode);
    chips.push({
      key: "toPay",
      to: `/c/${companySlug}/purchasing/to-pay`,
      label: "To pay",
      value:
        money != null
          ? `${glance.toPay.count} · ${money}`
          : String(glance.toPay.count),
    });
  }
  if (glance.toReceive) {
    chips.push({
      key: "toReceive",
      to: `/c/${companySlug}/purchasing/to-receive`,
      label: "To receive",
      value: String(glance.toReceive.count),
    });
  }

  if (chips.length === 0) return null;

  return (
    <section className="mt-6" aria-label="Owner glance">
      <div className="flex flex-wrap gap-2">
        {chips.map((chip) => (
          <Link
            key={chip.key}
            to={chip.to}
            className="inline-flex min-w-[7.5rem] flex-col rounded-[var(--radius-xl)] border border-[var(--color-border)]/90 bg-[var(--color-card)]/90 px-3 py-2.5 no-underline shadow-[var(--shadow-soft)] transition-colors hover:border-[var(--color-primary)]/40"
          >
            <span className="text-[11px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
              {chip.label}
            </span>
            <span className="mt-0.5 font-[family-name:var(--font-display)] text-lg font-semibold tabular-nums tracking-tight text-[var(--color-foreground)]">
              {chip.value}
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
